import React from 'react';
import store from '../store';
import VerifyEmailWall from '../components/VerifyEmailWall';
import * as Sentry from '@sentry/react';
import styled from '@emotion/styled';
import Container from '../components/blocks/Container';
import useModal from './useModal';
import useMediaBreakpoints from './useMediaBreakpoints';
import { useDispatch } from 'react-redux';
import { storeEmailVerifyWallData } from '../actions/authActions';
import { isDarkMode } from '../utils/helpers';
import breakpoints from '../theme/material/breakpoints';

const StyledContainer = styled(Container)({
  [breakpoints.up(769)]: {
    padding: '0',
  },
});

interface IUseVerifyEmailWallModal {
  showMandatoryVerifyEmailWall(callback?: () => void, callbackAfterValidate?: () => void, action?: string): void;
  isVerifyEmailRequired(account: any): boolean;
}

const useVerifyEmailWallModal = (): IUseVerifyEmailWallModal => {
  const { basicModal, resetModal } = useModal();
  const { isMobile } = useMediaBreakpoints();
  const dispatch = useDispatch();

  const showMandatoryVerifyEmailWall = (callback = () => undefined, callbackAfterValidate = () => undefined) => {
    try {
      const accountState = store.getState()?.profile ?? null;

      if (accountState === null) {
        return;
      }

      const shouldVerifyEmail = isVerifyEmailRequired(accountState);

      if (!shouldVerifyEmail) {
        callback();
        return;
      }

      basicModal({
        dateTestId: 'verify-email-modal',
        modalContentLabel: 'Verify Email Reminder',
        isModalOpen: true,
        withoutBgPattern: true,
        closeOnOverlayClick: false,
        modalBody: (
          <StyledContainer>
            <VerifyEmailWall
              handleCancel={() => {
                resetModal();
                callbackAfterValidate();
                dispatch(storeEmailVerifyWallData(null));
              }}
              onCompletionAction={() => {
                resetModal();
                callback();
              }}
            />
          </StyledContainer>
        ),
        customContentStyle: { maxWidth: isMobile ? 420 : 500, ...(!isDarkMode() ? { background: '#FFF' } : {}) },
        customOverlayStyle: { zIndex: 10001 },
        onCloseClickHandler: () => {
          callbackAfterValidate();
          dispatch(storeEmailVerifyWallData(null));
        },
        enableTouchMove: true,
      });
    } catch (error) {
      Sentry.captureException(error, {
        tags: { component: 'useVerifyEmailWallModal', action: 'showMandatoryVerifyEmailWall' },
      });
    }
  };

  const isVerifyEmailRequired = (account): boolean => {
    if (typeof account.email_mandatory_completed === 'undefined') {
      return false;
    }

    return !account.email_mandatory_completed;
  };

  return {
    showMandatoryVerifyEmailWall,
    isVerifyEmailRequired,
  };
};

export default useVerifyEmailWallModal;
